import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import Board from './Board';
import NextTetromino from './NextTetromino';
import GameInfo from './GameInfo';
import MobileControls from './MobileControls';
import TouchController from './TouchController';
import { 
  SHAPES, 
  COLORS, 
  SPEEDS, 
  KEYS,
  BOARD_WIDTH,
  BOARD_HEIGHT,
  CELL_SIZE
} from '../utils/constants';
import { 
  createEmptyBoard, 
  checkCollision, 
  mergeTetrominoWithBoard, 
  rotateShape,
  clearCompletedRows,
  calculateScore
} from '../utils/gameHelpers';

const GameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  color: #fff;
  background: #222;
  min-height: 100vh;
  font-family: Arial, Helvetica, sans-serif;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 20px 10px;
  }
`;

const GameTitle = styled.h1`
  margin-bottom: 30px;
  font-size: 2.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 15px;
  }
`;

const GameArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const BoardWrapper = styled.div`
  position: relative;
  
  @media (max-width: 768px) {
    transform: scale(${props => props.mobileScale});
    transform-origin: top center;
    margin-bottom: ${props => -((1 - props.mobileScale) * BOARD_HEIGHT * CELL_SIZE)}px;
  }
`;

const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row;
    justify-content: space-around;
    flex-wrap: wrap;
    margin-top: 10px;
  }
`;

const InfoWrapper = styled.div`
  @media (max-width: 768px) {
    width: 48%;
  }
`;

const NextTetrominoWrapper = styled.div`
  @media (max-width: 768px) {
    width: 48%;
  }
`;

const GameControls = styled.div`
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    margin-top: 15px;
    flex-direction: row;
    width: 100%;
    justify-content: center;
  }
`;

const StyledButton = styled.button`
  padding: 10px 20px;
  margin: 10px;
  border: none;
  border-radius: 4px;
  background: #00BFFF;
  color: white;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #0099CC;
  }
  
  &:disabled {
    background: #888;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    margin: 5px;
    font-size: 0.9rem;
  }
`;

const ControlsInfo = styled.div`
  margin-top: 20px;
  padding: 15px;
  background: #333;
  border-radius: 4px;
  
  p {
    margin: 5px 0;
    font-size: 0.9rem;
  }
  
  @media (max-width: 768px) {
    display: none; /* 在移动端隐藏键盘控制信息 */
  }
`;

const Game = () => {
  const [board, setBoard] = useState(createEmptyBoard());
  const [tetromino, setTetromino] = useState(null);
  const [nextTetromino, setNextTetromino] = useState(null);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lines, setLines] = useState(0);
  const [isGameOver, setIsGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [dropTime, setDropTime] = useState(SPEEDS[1]);
  const [dropCounter, setDropCounter] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileScale, setMobileScale] = useState(1);

  const gameStateRef = useRef({
    board,
    tetromino,
    nextTetromino,
    score,
    level,
    isPaused,
    isGameOver
  });

  useEffect(() => {
    gameStateRef.current = {
      board,
      tetromino,
      nextTetromino,
      score,
      level,
      isPaused,
      isGameOver
    };
  }, [board, tetromino, nextTetromino, score, level, isPaused, isGameOver]);

  const generateTetromino = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * SHAPES.length);
    const shape = SHAPES[randomIndex];
    
    return {
      shape,
      colorIndex: randomIndex,
      x: Math.floor(BOARD_WIDTH / 2) - Math.floor(shape[0].length / 2),
      y: 0
    };
  }, []);

  const initGame = useCallback(() => {
    setBoard(createEmptyBoard());
    const firstTetromino = generateTetromino();
    const secondTetromino = generateTetromino();
    setTetromino(firstTetromino);
    setNextTetromino(secondTetromino);
    setScore(0);
    setLevel(1);
    setLines(0);
    setIsGameOver(false);
    setIsPaused(false);
    setDropTime(SPEEDS[1]);
  }, [generateTetromino]);

  const moveTetromino = useCallback((x, y) => {
    const { 
      board, 
      tetromino, 
      nextTetromino, 
      score, 
      level, 
      isPaused, 
      isGameOver 
    } = gameStateRef.current;
    
    if (isPaused || isGameOver || !tetromino) return;
    
    const newPosition = {
      ...tetromino,
      x: tetromino.x + x,
      y: tetromino.y + y
    };
    
    if (!checkCollision(board, newPosition, { x: 0, y: 0 })) {
      setTetromino(newPosition);
    } else if (y > 0) {
      if (tetromino.y < 1) {
        setIsGameOver(true);
        return;
      }
      
      const newBoard = mergeTetrominoWithBoard(board, tetromino);
      const { newBoard: clearedBoard, clearedRows } = clearCompletedRows(newBoard);
      
      if (clearedRows > 0) {
        const newScore = score + calculateScore(clearedRows, level);
        setScore(newScore);
        setLines(prevLines => {
          const newLines = prevLines + clearedRows;
          if (Math.floor(newLines / 10) > Math.floor(prevLines / 10)) {
            const newLevel = Math.min(10, Math.floor(newLines / 10) + 1);
            setLevel(newLevel);
            setDropTime(SPEEDS[newLevel]);
          }
          return newLines;
        });
      }
      
      setBoard(clearedBoard);
      setTetromino(nextTetromino);
      setNextTetromino(generateTetromino());
    }
  }, [generateTetromino]);

  const rotateTetromino = useCallback(() => {
    const { board, tetromino, isPaused, isGameOver } = gameStateRef.current;
    
    if (isPaused || isGameOver || !tetromino) return;
    
    const rotatedShape = rotateShape(tetromino.shape);
    const newTetromino = {
      ...tetromino,
      shape: rotatedShape
    };
    
    if (!checkCollision(board, newTetromino, { x: 0, y: 0 })) {
      setTetromino(newTetromino);
    }
  }, []);

  const dropTetromino = useCallback(() => {
    moveTetromino(0, 1);
  }, [moveTetromino]);

  const hardDropTetromino = useCallback(() => {
    const { board, tetromino, isPaused, isGameOver } = gameStateRef.current;
    
    if (isPaused || isGameOver || !tetromino) return;
    
    let newY = tetromino.y;
    while (!checkCollision(board, { ...tetromino, y: newY + 1 }, { x: 0, y: 0 })) {
      newY++;
    }
    
    setTetromino({ ...tetromino, y: newY });
    moveTetromino(0, 1);
  }, [moveTetromino]);

  const handleKeyDown = useCallback((e) => {
    e.preventDefault();
    
    const { isGameOver, isPaused } = gameStateRef.current;
    
    if (isGameOver) return;
    
    if (e.key === KEYS.P) {
      setIsPaused(prev => !prev);
      return;
    }
    
    if (isPaused) return;
    
    switch (e.key) {
      case KEYS.LEFT:
        moveTetromino(-1, 0);
        break;
      case KEYS.RIGHT:
        moveTetromino(1, 0);
        break;
      case KEYS.DOWN:
        dropTetromino();
        break;
      case KEYS.UP:
        rotateTetromino();
        break;
      case KEYS.SPACE:
        hardDropTetromino();
        break;
      default:
        break;
    }
  }, [moveTetromino, dropTetromino, rotateTetromino, hardDropTetromino]);

  useEffect(() => {
    let lastTime = 0;
    let animationId;
    
    const gameLoop = (time = 0) => {
      const deltaTime = time - lastTime;
      lastTime = time;
      
      const { isPaused, isGameOver, tetromino } = gameStateRef.current;
      
      if (!isPaused && !isGameOver && tetromino) {
        setDropCounter(prevCounter => {
          const newCounter = prevCounter + deltaTime;
          
          if (newCounter > dropTime) {
            moveTetromino(0, 1);
            return 0;
          }
          
          return newCounter;
        });
      }
      
      animationId = requestAnimationFrame(gameLoop);
    };
    
    animationId = requestAnimationFrame(gameLoop);
    
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [dropTime, moveTetromino]);

  useEffect(() => {
    initGame();
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [initGame, handleKeyDown]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      if (mobile) {
        const screenWidth = window.innerWidth - 20;
        const boardWidth = BOARD_WIDTH * CELL_SIZE;
        const scale = Math.max(0.5, Math.min(1, screenWidth / boardWidth));
        setMobileScale(scale);
      } else {
        setMobileScale(1);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);

  return (
    <GameWrapper>
      <GameTitle>俄罗斯方块</GameTitle>
      
      <GameArea>
        <BoardWrapper mobileScale={mobileScale}>
          <Board board={board} activeTetromino={tetromino} />
          {isMobile && tetromino && (
            <TouchController 
              onMove={moveTetromino}
              onRotate={rotateTetromino}
              onHardDrop={hardDropTetromino}
            />
          )}
        </BoardWrapper>
        
        <SidePanel>
          <InfoWrapper>
            <GameInfo 
              score={score}
              level={level}
              lines={lines}
              isGameOver={isGameOver}
            />
          </InfoWrapper>
          
          <NextTetrominoWrapper>
            <h3 style={{ textAlign: 'center', marginTop: '20px' }}>下一个</h3>
            {nextTetromino && (
              <NextTetromino 
                shape={nextTetromino.shape} 
                colorIndex={nextTetromino.colorIndex} 
              />
            )}
          </NextTetrominoWrapper>
          
          <GameControls>
            <StyledButton onClick={() => initGame()}>
              {isGameOver ? '重新开始' : '新游戏'}
            </StyledButton>
            
            <StyledButton 
              onClick={togglePause} 
              disabled={isGameOver}
            >
              {isPaused ? '继续' : '暂停'}
            </StyledButton>
          </GameControls>
          
          <ControlsInfo>
            <p>⬅️ ➡️ : 左右移动</p>
            <p>⬆️ : 旋转</p>
            <p>⬇️ : 加速下落</p>
            <p>空格 : 立即下落</p>
            <p>P : 暂停/继续</p>
          </ControlsInfo>
        </SidePanel>
      </GameArea>
      
      {isMobile && (
        <MobileControls 
          onMove={moveTetromino}
          onRotate={rotateTetromino}
          onDrop={() => moveTetromino(0, 1)}
          onHardDrop={hardDropTetromino}
          onPause={togglePause}
        />
      )}
    </GameWrapper>
  );
};

export default Game;